import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHero, SectionHidden } from "@/components/common";
import { usePageContent } from "@/hooks/usePageContent";
import { CoachTip } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import {
  HeartPulse, Brain, PiggyBank, Activity, Phone, Leaf, Coffee, ShieldCheck,
  Sparkles, ArrowRight, Users, BookOpen, Angry, Frown, Meh, Smile, Laugh, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const MOODS = [
  { v: 1, icon: Angry, label: "Struggling", color: "#ef4444" },
  { v: 2, icon: Frown, label: "Low", color: "#f59e0b" },
  { v: 3, icon: Meh, label: "OK", color: "#eab308" },
  { v: 4, icon: Smile, label: "Good", color: "#22c55e" },
  { v: 5, icon: Laugh, label: "Great", color: "#10b981" },
];

const RES_META = [
  { icon: Brain, tone: "violet", actionLabel: "Explore resources", linkKey: "r1_link" },
  { icon: PiggyBank, tone: "emerald", actionLabel: "Money help", linkKey: "r2_link" },
  { icon: Activity, tone: "sky", actionLabel: null, linkKey: null },
  { icon: Leaf, tone: "teal", actionLabel: "View my leave", to: "/requests" },
];

const TIP_ICONS = [Coffee, Users, BookOpen, Sparkles];

const TONE = {
  violet: "bg-violet-50 text-violet-600", emerald: "bg-emerald-50 text-emerald-600",
  sky: "bg-sky-50 text-[#2EA3FD]", teal: "bg-teal-50 text-teal-600",
};

function MoodCheckin() {
  const [today, setToday] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/wellbeing/my-checkins").then((r) => { setToday(r.data.today); if (r.data.today) setSaved(true); }).catch(() => {}); }, []);
  const submit = async (mood) => {
    try { await api.post("/wellbeing/checkin", { mood }); setToday({ mood }); setSaved(true); toast.success("Thanks for checking in — this stays private."); }
    catch { toast.error("Could not save your check-in"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="wellbeing-checkin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><HeartPulse size={17} className="text-[#36776B]" />How are you feeling today?</h2>
          <p className="text-sm text-slate-500 mt-1">A quick, private check-in. Only anonymous trends are ever shared with HR — never your individual answer.</p>
        </div>
        {saved && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="checkin-saved">Checked in today</span>}
      </div>
      <div className="flex flex-wrap gap-3 mt-5">
        {MOODS.map((m) => {
          const on = today?.mood === m.v;
          return (
            <button key={m.v} onClick={() => submit(m.v)} data-testid={`checkin-mood-${m.v}`}
              className={`flex flex-col items-center gap-1.5 rounded-xl border px-5 py-3 transition-all hover:-translate-y-0.5 ${on ? "border-transparent text-white shadow-md" : "border-slate-200 bg-white hover:border-slate-300 text-slate-500"}`}
              style={on ? { backgroundColor: m.color } : {}}>
              <m.icon size={26} style={on ? {} : { color: m.color }} />
              <span className="text-xs font-medium">{m.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HrTrend() {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/wellbeing/trend").then((r) => setT(r.data)).catch(() => setT(null)); }, []);
  if (!t) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="wellbeing-trend">
      <h2 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><BarChart3 size={17} className="text-[#2EA3FD]" />Team wellbeing trend <span className="text-[11px] font-medium text-slate-400">(anonymous · last 30 days)</span></h2>
      {!t.enough_data ? (
        <p className="text-sm text-slate-500 mt-3" data-testid="trend-insufficient">Not enough check-ins yet to show a trend. We only reveal aggregates once at least {t.min_responses} have been logged, to protect everyone's privacy. ({t.responses} so far.)</p>
      ) : (
        <div className="mt-4">
          <div className="flex flex-wrap gap-6 mb-4">
            <div><p className="text-xs text-slate-400">Average mood</p><p className="text-2xl font-extrabold text-[#0B1A2F]" data-testid="trend-average">{t.average} / 5</p></div>
            <div><p className="text-xs text-slate-400">Check-ins</p><p className="text-2xl font-extrabold text-[#0B1A2F]">{t.responses}</p></div>
            <div><p className="text-xs text-slate-400">People</p><p className="text-2xl font-extrabold text-[#0B1A2F]">{t.participants}</p></div>
          </div>
          <div className="space-y-2">
            {MOODS.slice().reverse().map((m) => {
              const count = t.distribution[m.v] || 0;
              const pct = t.responses ? Math.round((count / t.responses) * 100) : 0;
              return (
                <div key={m.v} className="flex items-center gap-3" data-testid={`trend-bar-${m.v}`}>
                  <m.icon size={16} style={{ color: m.color }} className="shrink-0" />
                  <span className="text-xs text-slate-500 w-16 shrink-0">{m.label}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: m.color }} /></div>
                  <span className="text-xs text-slate-400 w-10 text-right shrink-0">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Wellbeing() {
  const { user } = useAuth();
  const pc = usePageContent();
  const isHr = ["admin", "hr_admin"].includes(user?.role);
  const wb = pc?.wellbeing || {};
  const eap = wb.eap || {};
  const tips = wb.tips || {};
  const talk = wb.talk || {};
  const cards = [
    { ...RES_META[0], sec: wb.resource1 || {} },
    { ...RES_META[1], sec: wb.resource2 || {} },
    { ...RES_META[2], sec: wb.resource3 || {} },
    { ...RES_META[3], sec: wb.resource4 || {} },
  ];
  const tipList = [tips.tip1, tips.tip2, tips.tip3, tips.tip4];
  return (
    <div className="space-y-6" data-testid="wellbeing-page">
      <CoachTip tipKey="wellbeing" title="Your check-in is private">Log how you're feeling in seconds. Only anonymous team trends are shared with HR — never your individual answer.</CoachTip>
      <PageHero
        title="Your wellbeing matters"
        subtitle="At CASS we want everyone to feel supported — in and out of work. Explore confidential help, healthy habits and the people here for you."
        icon={HeartPulse}
        tone="teal"
        eyebrow="Wellbeing"
        pageKey="wellbeing"
      />

      <MoodCheckin />
      {isHr && <HrTrend />}

      <SectionHidden enabled={eap.enabled} label="EAP / helpline card" />
      {eap.enabled !== false && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="wellbeing-eap">
          <div className="grid md:grid-cols-3">
            <div className="bg-gradient-to-br from-[#36776B] to-[#2C6055] text-white p-6 flex flex-col justify-center">
              <span className="rounded-lg bg-white/15 w-11 h-11 grid place-items-center mb-3"><Phone size={20} /></span>
              <p className="text-xs uppercase tracking-widest text-white/70">{eap.phone_label || "24/7 confidential helpline"}</p>
              <p className="text-2xl font-extrabold mt-1">{eap.phone || "0800 123 4567"}</p>
            </div>
            <div className="md:col-span-2 p-6">
              <h2 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><ShieldCheck size={17} className="text-[#36776B]" />{eap.heading || "Employee Assistance Programme (EAP)"}</h2>
              <p className="text-sm text-slate-600 mt-2">{eap.body}</p>
              {eap.footnote && <p className="text-xs text-slate-400 mt-3">{eap.footnote}</p>}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="wellbeing-resources">
        {cards.map((c, i) => {
          const r = c.sec;
          if (r.enabled === false) return <SectionHidden key={i} enabled={r.enabled} label={`${r.title || `Resource ${i + 1}`} card`} />;
          const Icon = c.icon;
          const href = c.linkKey ? r.link : null;
          return (
            <div key={i} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col hover:shadow-md transition-shadow" data-testid={`wellbeing-resource-${i}`}>
              <div className="flex items-start gap-3">
                <span className={`rounded-lg p-2.5 shrink-0 ${TONE[c.tone]}`}><Icon size={20} /></span>
                <div>
                  <h3 className="font-semibold text-[#0B1A2F]">{r.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{r.desc}</p>
                </div>
              </div>
              {c.actionLabel && (c.to || href) && (
                <div className="mt-4">
                  {c.to ? (
                    <Button asChild variant="outline" size="sm" data-testid={`wellbeing-action-${i}`}><Link to={c.to}>{c.actionLabel}<ArrowRight size={14} className="ml-1.5" /></Link></Button>
                  ) : (
                    <Button asChild variant="outline" size="sm" data-testid={`wellbeing-action-${i}`}><a href={href} target="_blank" rel="noreferrer noopener">{c.actionLabel}<ArrowRight size={14} className="ml-1.5" /></a></Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <SectionHidden enabled={tips.enabled} label="'Small habits' tips" />
      {tips.enabled !== false && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="wellbeing-tips">
          <h2 className="font-semibold text-[#0B1A2F] flex items-center gap-2 mb-4"><Sparkles size={17} className="text-[#2EA3FD]" />{tips.heading || "Small habits, big difference"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {tipList.map((t, i) => {
              const Icon = TIP_ICONS[i];
              return (
                <div key={i} className="flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-3" data-testid={`wellbeing-tip-${i}`}>
                  <span className="rounded-full bg-white p-2 text-[#36776B] shadow-sm"><Icon size={16} /></span>
                  <p className="text-sm text-slate-600">{t}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <SectionHidden enabled={talk.enabled} label="'Talk to someone' note" />
      {talk.enabled !== false && (
        <div className="bg-[#F5FAFF] border border-sky-100 rounded-xl p-5 text-sm text-slate-600 flex items-start gap-3" data-testid="wellbeing-talk">
          <Users size={18} className="text-[#2EA3FD] shrink-0 mt-0.5" />
          <p>{talk.body}</p>
        </div>
      )}
    </div>
  );
}
